// Traductions du mode Lite : chaînes communes (navigation, filtres, mode, formats).
export const fr = {
  // Noms de page en japonais (thème Tokyo Night). Décor volontaire : identiques en FR et EN.
  kana: {
    dashboard: "ダッシュボード",
    propFirms: "プロップファーム",
    accounts: "口座",
    journal: "取引日誌",
    sessions: "セッション",
    stats: "統計",
    edgy: "AI分析",
    discipline: "規律",
    reports: "レポート",
  },
  nav: {
    dashboard: "Tableau de bord",
    dashboardShort: "Dashboard",
    journal: "Journal",
    stats: "Stats",
    propFirms: "Prop Firm Manager",
    propFirmsShort: "Prop firms",
    accounts: "Comptes",
    sessions: "Sessions",
    discipline: "Discipline",
    edgy: "Edgy",
    edgyShort: "Edgy",
    reports: "Rapports",
    settings: "Réglages",
    addTrade: "Ajouter un trade",
    lightMode: "Mode clair",
    darkMode: "Mode sombre",
    liteBadge: "Lite",
  },
  periods: {
    today: "Aujourd'hui",
    todayShort: "Jour",
    week: "Cette semaine",
    weekShort: "Semaine",
    month: "30 derniers jours",
    monthShort: "30 j",
    threeMonths: "3 derniers mois",
    threeMonthsShort: "3 mois",
    sixMonths: "6 derniers mois",
    sixMonthsShort: "6 mois",
    all: "Tout",
    allShort: "Tout",
  },
  filters: {
    propFirm: "Prop firm",
    allPropFirms: "Toutes les prop firms",
    personal: "Comptes perso",
    account: "Compte",
    allAccounts: "Tous les comptes",
    activeAccounts: "Comptes actifs",
    challengesOnly: "Challenges uniquement",
    fundedOnly: "Funded uniquement",
    active: "Actifs",
    inactive: "Inactifs",
    strategy: "Stratégie",
    allStrategies: "Toutes les stratégies",
    reset: "Réinitialiser",
  },
  mode: {
    title: "Mode d'affichage",
    lite: "Lite",
    advanced: "Advanced",
    liteDesc: "L'essentiel, sans réglages.",
    advancedDesc: "Toute la puissance d'Edgely.",
    litePoints: ["Pages prêtes à l'emploi", "Une seule barre de filtres", "Idéal pour démarrer"],
    advancedPoints: ["Dashboard personnalisable par widgets", "Pages personnalisées, notebook, sessions", "Discipline, playbook, rapports"],
    lockedBadge: "Pro et Max",
    lockedShort: "Pro",
    subtitlePaid: "Change quand tu veux : tes données restent les mêmes.",
    subtitleFree: "Le mode Advanced est inclus dans les plans Pro et Max.",
    upgrade: "Passer à Pro",
    allSettings: "Tous les réglages",
    switchToLite: "Passer en mode Lite",
    switchToAdvanced: "Passer en mode Advanced",
  },
  intro: {
    title: "Bienvenue dans le nouvel Edgely",
    lead: "Edgely passe en mode Lite : les mêmes données, rangées dans 5 pages claires. Plus besoin de configurer ton dashboard.",
    points: [
      "Tableau de bord, Journal, Stats, Prop Firm Manager, Comptes",
      "Une seule barre de filtres, partagée entre toutes les pages",
      "Des stats rangées par question, avec ce qu'il faut retenir",
    ],
    backTitle: "Tu préfères l'ancienne interface ?",
    backText: "Reviens au mode Advanced à tout moment : menu en bas à gauche, ou Réglages, puis Mode d'affichage. Tes layouts et pages personnalisées t'y attendent, intacts.",
    freeNote: "Le mode Advanced (dashboard personnalisable) est inclus dans les plans Pro et Max.",
    cta: "C'est parti",
  },
  charts: {
    payouts: "Payouts",
    expenses: "Dépenses",
    cumulative: "Net cumulé",
    drawdown: "Drawdown",
    winners: "Gagnant",
    losers: "Perdant",
    tradeCountHint: "Chiffre = nombre de trades",
    heatmapCell: (day: string, hour: string, pnl: string, n: number) => `${day} ${hour} : ${pnl} sur ${n} trade${n > 1 ? "s" : ""}`,
    heatmapEmpty: (day: string, hour: string) => `${day} ${hour} : aucun trade`,
  },
  blocks: {
    takeaways: "À retenir",
    trades: "Trades",
    winners: "Gagnants",
    winnersShort: "Gagn.",
    pf: "PF",
    average: "Moyenne",
    result: "Résultat",
    total: "Total",
    winnersAndTrades: (n: number) => `gagnants · ${n} trades`,
  },
  trade: {
    edit: "Modifier",
    delete: "Supprimer",
    dayMeta: (n: number, winners: string, from: string, to: string) => `${n} trade${n > 1 ? "s" : ""} · ${winners} gagnants · ${from} à ${to}`,
    showPrevious: (n: number) => `Voir les jours précédents (${n} restants)`,
    note: "Note",
    open: "Ouvert",
    netResult: "Résultat net",
    grossAndFees: (gross: string, fees: string) => `Brut ${gross} · frais ${fees}`,
    entry: "Entrée",
    exit: "Sortie",
    quantity: "Quantité",
    points: "Points",
    duration: "Durée",
    exitAt: "Sortie à",
    strategy: "Stratégie",
    noStrategy: "Aucune stratégie",
    strategyHint: "La stratégie suit celle du compte.",
    rating: "Note du setup",
    tags: "Tags",
    noTags: "Aucun tag pour l'instant. Crée-les depuis le détail complet du trade.",
    notePlaceholder: "Qu'as-tu vu ? Qu'aurais-tu fait différemment ?",
    fullDetail: "Détail complet (captures, champs perso)",
    noAccount: "Sans compte",
    saved: "Enregistré",
    contracts: (n: number) => `${n} contrat${n > 1 ? "s" : ""}`,
  },
  account: {
    spent: "Dépensé",
    payouts: "Payouts",
    roi: "ROI",
    breached: "Perte max dépassée. Compte clôturé.",
    inactive: "Compte inactif.",
  },
  common: {
    close: "Fermer",
    cancel: "Annuler",
    save: "Enregistrer",
    seeAll: "Tout voir",
    previousMonth: "Mois précédent",
    nextMonth: "Mois suivant",
    noTrade: "Aucun trade",
    noTradeHint: "Change de période ou de filtres pour en voir.",
    noTradeInPeriod: "Aucun trade sur cette période",
    noTradeInPeriodHint: "Élargis la période ou retire un filtre.",
    loading: "Chargement…",
    long: "Long",
    short: "Short",
    trades: (n: number) => `${n} trade${n > 1 ? "s" : ""}`,
    days: (n: number) => `${n} jour${n > 1 ? "s" : ""}`,
    winners: "gagnants",
    weekdaysShort: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
    weekdaysLong: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"],
    hour: (h: number) => `${h} h`,
    lowData: "Tes stats deviennent fiables à partir d'une vingtaine de trades.",
    statuses: {
      challenge: "Challenge",
      funded: "Funded",
      live: "Live",
      personal: "Perso",
      demo: "Démo",
      inactive: "Inactif",
      breached: "Cramé",
    },
  },
};

export const en: typeof fr = {
  // Noms de page en japonais (thème Tokyo Night). Décor volontaire : identiques en FR et EN.
  kana: {
    dashboard: "ダッシュボード",
    propFirms: "プロップファーム",
    accounts: "口座",
    journal: "取引日誌",
    sessions: "セッション",
    stats: "統計",
    edgy: "AI分析",
    discipline: "規律",
    reports: "レポート",
  },
  nav: {
    dashboard: "Dashboard",
    dashboardShort: "Dashboard",
    journal: "Journal",
    stats: "Stats",
    propFirms: "Prop Firm Manager",
    propFirmsShort: "Prop firms",
    accounts: "Accounts",
    sessions: "Sessions",
    discipline: "Discipline",
    edgy: "Edgy",
    edgyShort: "Edgy",
    reports: "Reports",
    settings: "Settings",
    addTrade: "Add a trade",
    lightMode: "Light mode",
    darkMode: "Dark mode",
    liteBadge: "Lite",
  },
  periods: {
    today: "Today",
    todayShort: "Day",
    week: "This week",
    weekShort: "Week",
    month: "Last 30 days",
    monthShort: "30d",
    threeMonths: "Last 3 months",
    threeMonthsShort: "3m",
    sixMonths: "Last 6 months",
    sixMonthsShort: "6m",
    all: "All",
    allShort: "All",
  },
  filters: {
    propFirm: "Prop firm",
    allPropFirms: "All prop firms",
    personal: "Personal accounts",
    account: "Account",
    allAccounts: "All accounts",
    activeAccounts: "Active accounts",
    challengesOnly: "Challenges only",
    fundedOnly: "Funded only",
    active: "Active",
    inactive: "Inactive",
    strategy: "Strategy",
    allStrategies: "All strategies",
    reset: "Reset",
  },
  mode: {
    title: "Display mode",
    lite: "Lite",
    advanced: "Advanced",
    liteDesc: "The essentials, no setup.",
    advancedDesc: "The full power of Edgely.",
    litePoints: ["Ready-to-use pages", "A single filter bar", "Perfect to get started"],
    advancedPoints: ["Widget-based customizable dashboard", "Custom pages, notebook, sessions", "Discipline, playbook, reports"],
    lockedBadge: "Pro and Max",
    lockedShort: "Pro",
    subtitlePaid: "Switch anytime: your data stays the same.",
    subtitleFree: "Advanced mode is included in the Pro and Max plans.",
    upgrade: "Upgrade to Pro",
    allSettings: "All settings",
    switchToLite: "Switch to Lite mode",
    switchToAdvanced: "Switch to Advanced mode",
  },
  intro: {
    title: "Welcome to the new Edgely",
    lead: "Edgely now opens in Lite mode: the same data, organized in 5 clear pages. No more dashboard setup.",
    points: [
      "Dashboard, Journal, Stats, Prop Firm Manager, Accounts",
      "A single filter bar, shared across every page",
      "Stats organized by question, with the key takeaways",
    ],
    backTitle: "Prefer the previous interface?",
    backText: "Switch back to Advanced mode anytime: bottom-left menu, or Settings, then Display mode. Your layouts and custom pages are waiting for you, untouched.",
    freeNote: "Advanced mode (customizable dashboard) is included in the Pro and Max plans.",
    cta: "Let's go",
  },
  charts: {
    payouts: "Payouts",
    expenses: "Expenses",
    cumulative: "Cumulative net",
    drawdown: "Drawdown",
    winners: "Winning",
    losers: "Losing",
    tradeCountHint: "Number = trade count",
    heatmapCell: (day: string, hour: string, pnl: string, n: number) => `${day} ${hour}: ${pnl} over ${n} trade${n > 1 ? "s" : ""}`,
    heatmapEmpty: (day: string, hour: string) => `${day} ${hour}: no trades`,
  },
  blocks: {
    takeaways: "Key takeaways",
    trades: "Trades",
    winners: "Winners",
    winnersShort: "Win.",
    pf: "PF",
    average: "Average",
    result: "Result",
    total: "Total",
    winnersAndTrades: (n: number) => `winners · ${n} trades`,
  },
  trade: {
    edit: "Edit",
    delete: "Delete",
    dayMeta: (n: number, winners: string, from: string, to: string) => `${n} trade${n > 1 ? "s" : ""} · ${winners} winners · ${from} to ${to}`,
    showPrevious: (n: number) => `Show previous days (${n} left)`,
    note: "Note",
    open: "Open",
    netResult: "Net result",
    grossAndFees: (gross: string, fees: string) => `Gross ${gross} · fees ${fees}`,
    entry: "Entry",
    exit: "Exit",
    quantity: "Quantity",
    points: "Points",
    duration: "Duration",
    exitAt: "Exit at",
    strategy: "Strategy",
    noStrategy: "No strategy",
    strategyHint: "The strategy follows the account's.",
    rating: "Setup rating",
    tags: "Tags",
    noTags: "No tags yet. Create them from the full trade detail.",
    notePlaceholder: "What did you see? What would you do differently?",
    fullDetail: "Full detail (screenshots, custom fields)",
    noAccount: "No account",
    saved: "Saved",
    contracts: (n: number) => `${n} contract${n > 1 ? "s" : ""}`,
  },
  account: {
    spent: "Spent",
    payouts: "Payouts",
    roi: "ROI",
    breached: "Max loss exceeded. Account closed.",
    inactive: "Inactive account.",
  },
  common: {
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    seeAll: "See all",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    noTrade: "No trades",
    noTradeHint: "Change the period or filters to see some.",
    noTradeInPeriod: "No trades in this period",
    noTradeInPeriodHint: "Widen the period or remove a filter.",
    loading: "Loading…",
    long: "Long",
    short: "Short",
    trades: (n: number) => `${n} trade${n > 1 ? "s" : ""}`,
    days: (n: number) => `${n} day${n > 1 ? "s" : ""}`,
    winners: "winners",
    weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    weekdaysLong: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    hour: (h: number) => `${h}:00`,
    lowData: "Your stats become reliable after about twenty trades.",
    statuses: {
      challenge: "Challenge",
      funded: "Funded",
      live: "Live",
      personal: "Personal",
      demo: "Demo",
      inactive: "Inactive",
      breached: "Breached",
    },
  },
};
